/* global axios */
import { useAlert } from 'dashboard/composables';
import { normalizeReplies } from '../../helper/coLivingReplies';

const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

const state = {
  chatbotName: '',
  condition: '',
  humanEmail: '',
  selectedLLM: {
    name: '',
    provider: '',
  },
  instruction: '',
  isBotToggled: false,
  isAppointmentToggled: false,
  isRentalToggled: false,
  messageDelayEnabled: false,
  messageDelaySeconds: 5,
  visionEnabled: false,
  attachmentNotificationEnabled: true,
  // inbox goal (DRP-111) — raw values from GET /config; the form copy lives in the component
  goalEnabled: false,
  goalInstruction: "",
  goalFields: [],
  goalCreditMultiplier: 2,
  appId: '',
  needUpdate: null,
  // platform type
  bot_service_type: 'dahreply',
  useCustomDify: false,
  customDifyApiToken: '',
  customDifyUrl: '',
  useCustomN8n: false,
  customN8nUrl: '',
  condo_management_enabled: false,
  condoId: null,
  // co-living mode: the toggle, the admin's replies (strings, empty = use the
  // default) and the server's default wording for each reply
  co_living_enabled: false,
  co_living_replies: normalizeReplies(null),
  co_living_reply_defaults: {},
  predefinedLabels: [],
  isChatbotAvailable: true,
  branchScope: {
    scopeType: 'all',
    branchIds: [],
    branches: [],
  },
  // neo4j connection inputs
  neo4jUri: '',
  neo4jUsername: '',
  neo4jPassword: '',
  neo4jDatabase: '',
  // datasets returned by config endpoint
  datasets: [],
  // simai
  simaiWorkflowId: '',
  // reply mode (auto | semi_auto) and AI Assist settings
  replyMode: 'auto',
  aiAssistTrigger: 'wait',
  aiAssistIdleSeconds: 300,
  // template appointment bot: { mode: 'legacy'|'template', module, available, status, entry_workflow_id }
  templateBot: null,
};

const getters = {
  chatbotConfig: state => state, // optional, in case you want to access all at once
};

const mutations = {
  SET_CHATBOT_CONFIG(state, config) {
    Object.assign(state, config);
  }
};

const actions = {
  async fetchChatbotConfig({ commit, rootGetters }, { inboxId }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const { data: response } = await axios.get(
        knowledgeProxy(accountId, `/chatbot/accounts/${accountId}/inboxes/${inboxId}/config`)
      );
      // Mask password based on backend boolean; never store real secret in state
      const maskedPassword = response.has_neo4j_password ? '********' : '';

      commit('SET_CHATBOT_CONFIG', {
        chatbotName: response.agent_bot_name,
        condition: response.agent_condition,
        humanEmail: response.agent_email,
        selectedLLM: {
          name: response.llm?.name || '',
          provider: response.llm?.provider || '',
        },
        instruction: response.instruction,
        isBotToggled: response.is_enabled,
        isAppointmentToggled: response.appointment_enabled,
        isRentalToggled: response.rental_enabled,
        messageDelayEnabled: response.message_delay_enabled || false,
        messageDelaySeconds: response.message_delay_seconds || 5,
        visionEnabled: response.vision_enabled || false,
        attachmentNotificationEnabled:
          response.attachment_notification_enabled ?? true,
        goalEnabled: response.goal_enabled || false,
        goalInstruction: response.goal_instruction || "",
        goalFields: response.goal_fields || [],
        goalCreditMultiplier: response.goal_credit_multiplier || 2,
        appId: response.app_id,
        needUpdate: response.need_upgrade || false,
        bot_service_type: response.bot_service_type || 'dahreply',
        useCustomDify: response.use_custom_dify || false,
        customDifyApiToken:  response.custom_dify_api_token || '',
        customDifyUrl:  response.custom_dify_url || '',
        useCustomN8n: response.use_custom_n8n || false,
        customN8nUrl:  response.custom_n8n_url || '',
        condo_management_enabled: response.condo_management_enabled || false,
        condoId: response.condo_id || null,
        co_living_enabled: response.co_living_enabled || false,
        co_living_replies: normalizeReplies(response.co_living_replies),
        co_living_reply_defaults: response.co_living_reply_defaults || {},
        predefinedLabels: response.predefined_labels || [],
        // neo4j fields (if present)
        neo4jUri: response.neo4j_uri || '',
        neo4jUsername: response.neo4j_username || '',
        neo4jPassword: maskedPassword,
        neo4jDatabase: response.neo4j_database || '',
        // datasets field
        datasets: response.datasets || [],
        // simai
        simaiWorkflowId: response.simai_workflow_id || '',
        replyMode: response.reply_mode || 'auto',
        aiAssistTrigger: response.ai_assist_trigger || 'wait',
        aiAssistIdleSeconds: response.ai_assist_idle_seconds || 300,
        templateBot: response.template_bot || null,
        branchScope: {
          scopeType: response.branch_scope?.scope_type || 'all',
          branchIds: response.branch_scope?.branch_ids || [],
          branches: response.branch_scope?.branches || [],
        },
      });
    } catch (err) {
      throw err;
    }
  },

  async fetchChatbot({ commit, rootGetters }, { inboxId }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const response = await axios.get(
        knowledgeProxy(accountId, `/chatbot/accounts/${accountId}/inboxes/${inboxId}/app`)
      );
      commit('SET_CHATBOT_CONFIG', { isChatbotAvailable: response.status >= 200 && response.status < 300 });
    } catch (err) {
      // axios throws on non-2xx; treat as unavailable
      commit('SET_CHATBOT_CONFIG', { isChatbotAvailable: false });
      console.error(err);
      useAlert('Error connecting to server, please try again later.');
    }
  },

  // Lightweight existence check for gating purposes (e.g. onboarding step
  // 3's "unskippable" Continue) — unlike fetchChatbot above, a failure here
  // just means "no chatbot for this inbox yet" (the normal state right
  // after inbox creation, before/while the auto-create call resolves), not
  // a real error, so it doesn't alert and doesn't touch shared
  // chatbotConfig state.
  async checkChatbotExists({ rootGetters }, { inboxId }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const response = await axios.get(
        knowledgeProxy(
          accountId,
          `/chatbot/accounts/${accountId}/inboxes/${inboxId}/app`
        )
      );
      return response.status >= 200 && response.status < 300;
    } catch (err) {
      return false;
    }
  },

  async createChatbot({ rootGetters }, { inboxId }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      await axios.post(
        knowledgeProxy(accountId, `/chatbot/accounts/${accountId}/inboxes/${inboxId}/app`)
      );
      window.location.reload();
    } catch (err) {
      useAlert('Error creating chatbot, please try again later.');
    }
  },

  // Throws on failure so the caller can revert its optimistic UI.
  async setReplyMode(
    { commit, rootGetters },
    { accountId, inboxId, replyMode, aiAssistTrigger, aiAssistIdleSeconds }
  ) {
    const account = accountId || rootGetters.getCurrentAccountId;
    const { data } = await axios.put(
      knowledgeProxy(
        account,
        `/chatbot/accounts/${account}/inboxes/${inboxId}/reply_mode`
      ),
      {
        reply_mode: replyMode,
        ai_assist_trigger: aiAssistTrigger,
        ai_assist_idle_seconds: aiAssistIdleSeconds,
      }
    );
    // Fall back to the sent values if the response omits a field.
    commit('SET_CHATBOT_CONFIG', {
      replyMode: data?.reply_mode ?? replyMode,
      aiAssistTrigger: data?.ai_assist_trigger ?? aiAssistTrigger,
      aiAssistIdleSeconds: data?.ai_assist_idle_seconds ?? aiAssistIdleSeconds,
    });
    return data;
  },

  async toggleBot({ state, rootGetters, commit }, { inboxId }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const previousState = state.isBotToggled;
      await axios.post(
        knowledgeProxy(accountId, `/chatbot/accounts/${accountId}/inboxes/${inboxId}/toggle`)
      );
      commit('SET_CHATBOT_CONFIG', { isBotToggled: !previousState });
      useAlert(
        previousState
          ? "Bot disabled successfully"
          : "Bot enabled successfully"
      );
    } catch (err) {
      console.error(err);
      useAlert(err);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
